import { create } from 'zustand';
import { apiService } from '@/services/api';
import type { BankDto, BankListResponse } from '@/types';

/**
 * Logos come from `BankDto.logoLocation`, which is SIBS's public CDN URL
 * (contentsibs.azureedge.net) — directly browser-loadable, no auth. `BankLogo`
 * still prefers a self-hosted `/bank-logos/{aspspCde}.{svg,png}` override before
 * falling back to this URL.
 */
interface BankStore {
  // State
  banks: BankDto[];
  isLoading: boolean;
  error: string | null;
  cachedAt: string | null;

  // Actions
  fetchBanks: () => Promise<void>;
  clearError: () => void;
}

export const useBankStore = create<BankStore>((set, get) => ({
  // Initial state
  banks: [],
  isLoading: false,
  error: null,
  cachedAt: null,

  // Fetch available banks from SIBS API
  fetchBanks: async () => {
    // Don't fetch if we already have banks cached
    if (get().banks.length > 0 && get().cachedAt) {
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const response = await apiService.get<BankListResponse>('/banks');

      if (response.value) {
        set({
          banks: response.value.banks,
          cachedAt: response.value.cachedAt,
          isLoading: false,
        });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch banks',
        isLoading: false,
      });
    }
  },

  clearError: () => set({ error: null }),
}));
